// Rebook — sort and filter rules (pure, pinned by scripts/rebook-sort.test.mjs).
// The list's default order is the diary summary's (most overdue first); these
// are the owner's other ways of working the same list.

import { dueDate } from './planning'
import type { RoundCustomer } from './attention'

export type RebookSort = 'overdue' | 'value' | 'tier' | 'street' | 'urgent'

export const SORT_LABELS: Record<RebookSort, string> = {
  overdue: 'Most overdue',
  urgent: 'Most urgent',
  value: 'Highest value',
  tier: 'Tier',
  street: 'Street',
}

// "2.0", "Tier 1*", "1" → 2, 1, 1. Blank or a sentence ("havent had for AGES")
// → 99 so they sort last, never first.
export function tierRank(tier: string | null | undefined): number {
  const m = /\d+/.exec(String(tier ?? ''))
  return m ? Number(m[0]) : 99
}

export function tierLabel(tier: string | null | undefined): string {
  const n = tierRank(tier)
  return n === 99 ? '' : `Tier ${n}${/\*/.test(String(tier)) ? '*' : ''}`
}

// "Flat 4, 47 Church Crescent" → "church crescent"; "1A Coolhurst Road" →
// "coolhurst road". Good enough to put neighbours next to each other.
export function streetOf(address: string | null | undefined): string {
  let s = String(address ?? '').toLowerCase()
  // "Flat 1 + 2, 37 Woodland Gardens": the flat part is everything up to the
  // first comma when it starts with a flat word.
  s = s.replace(/^\s*(flat|apartment|apt|unit)\b[^,]*,\s*/g, '')
  s = s.replace(/\b(flat|apartment|apt|unit)\s*[\w-]+\s*,?\s*/g, ' ')
  s = s.replace(/^[\s,]*\d+[a-z]?(\s*[-–/+]\s*\d+[a-z]?)?\s*,?\s*/, '')
  s = s.split(',')[0] ?? s
  return s.replace(/\s+/g, ' ').trim()
}

export function overdueDays(c: RoundCustomer, today: string): number {
  const due = dueDate(c)
  if (!due) return 0
  const ms = new Date(`${today}T12:00:00`).getTime() - new Date(`${due}T12:00:00`).getTime()
  return Math.max(0, Math.round(ms / 86_400_000))
}

// Most urgent: the ones you are about to lose AND are worth most — going cold
// before slipping, then price, then how long overdue.
export function urgentKey(c: RoundCustomer, cold: boolean, today: string): [number, number, number] {
  return [cold ? 0 : 1, -(c.reg_price_pence ?? 0), -overdueDays(c, today)]
}

function cmpKeys(a: (number | string)[], b: (number | string)[]): number {
  for (let i = 0; i < a.length; i++) {
    const x = a[i]!, y = b[i]!
    if (x < y) return -1
    if (x > y) return 1
  }
  return 0
}

export function sortRows(rows: RoundCustomer[], sort: RebookSort, isCold: (c: RoundCustomer) => boolean, today: string): RoundCustomer[] {
  const key = (c: RoundCustomer): (number | string)[] => {
    switch (sort) {
      case 'value': return [-(c.reg_price_pence ?? -1), -overdueDays(c, today), c.id]
      case 'tier': return [tierRank(c.tier), -overdueDays(c, today), c.id]
      case 'street': return [streetOf(c.address) || '~', c.id]
      case 'urgent': return [...urgentKey(c, isCold(c), today), c.id]
      default: return [-overdueDays(c, today), c.id]
    }
  }
  return [...rows].sort((a, b) => cmpKeys(key(a), key(b)))
}
